import React, { useMemo, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters } from '../../../interfaces/property-visit.interface';
import { PropertyVisitKpis } from '../../../mappers/property-visit.mapper';
import { buildHeadCells, getStartEndOfMonth } from '../../../utils/methods';
import { VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING, APP_SUCCESS_MESSAGES, GENERIC_LOOKUP_KEYS, PROPERTY_VISIT_ROLE_TO_SYSTEM_ROLE } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';
import PropertyVisitService from '../../../services/property-visit.service';
import PropertyService from '../../../services/property.service';
import CommonService from '../../../services/common.service';
import VendorService from '../../../services/vendor.service';
import RoleService from '../../../services/role.service';
import UserService from '../../../services/user.service';
import { AuthContext } from '../../../contexts/AuthContext';
import { SortOrder, Modules, RoleCodes } from '../../../enums';
import { toast } from 'react-toastify';

const List: React.FC = () => {
  const { t } = useTranslation();
  const { loggedInUser } = useContext(AuthContext);

  const isManager = loggedInUser?.role_code === RoleCodes.manager;
  const isInspector = loggedInUser?.role_code === RoleCodes.inspector;

  const roleUserRole = isInspector ? RoleCodes.inspector : isManager ? 'property_manager' : '';
  const roleVisitedBy = (isInspector || isManager) ? (loggedInUser?.id ?? '') : '';

  const defaultFilters: ListFilters = {
    ...getStartEndOfMonth(),
    propertyId: '',
    visitType: '',
    userRole: roleUserRole,
    visitedBy: roleVisitedBy,
    visitStatus: '',
    searchText: '',
    managedByUserId: '',
  };

  const { initialFilters: savedFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>(
    'filters_property_visits',
    defaultFilters
  );

  // Always apply role-based pre-selection on top of any saved filters.
  // Memoized with empty deps so the reference stays stable — Filters.tsx resets
  // its local state whenever initialFilters reference changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialFilters: ListFilters = useMemo(() => (isManager || isInspector)
    ? { ...savedFilters, userRole: roleUserRole, visitedBy: roleVisitedBy }
    : savedFilters
    , []);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    propertyId: '',
    visitType: '',
    userRole: '',
    visitedBy: '',
    visitStatus: '',
    managedByUserId: '',
  });
  const [kpis, setKpis] = useState<PropertyVisitKpis>({
    to_be_reviewed: 0,
    needs_clarification: 0,
    completed: 0,
    not_qualified: 0,
  });
  const [removalId, setRemovalId] = useState<string>('');
  const [counter, setCounter] = useState<number>(0);

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.visitDate,
      VALIDATION_FIELD_MAPPING.propertyName,
      VALIDATION_FIELD_MAPPING.visitType,
      VALIDATION_FIELD_MAPPING.visitedBy,
      // VALIDATION_FIELD_MAPPING.visitNote,
      VALIDATION_FIELD_MAPPING.visitStatus,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'propertyVisitList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.desc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.visitDate,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: Modules.propertyVisits,
    uri: Modules.propertyVisits,
    perPage,
    page,
    order,
    orderBy,
  });

  const { total, data: visits, loading } = useFetch({
    service: PropertyVisitService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.propertyVisitList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [
      order, orderBy, page, perPage,
      filters.searchText, filters.propertyId, filters.visitType,
      filters.userRole, filters.visitedBy, filters.visitStatus,
      filters.startDate, filters.endDate, filters.managedByUserId,
      counter,
    ],
    onSuccess: (response: any) => {
      setKpis({
        to_be_reviewed: response.kpis?.to_be_reviewed ?? 0,
        needs_clarification: response.kpis?.needs_clarification ?? 0,
        completed: response.kpis?.completed ?? 0,
        not_qualified: response.kpis?.not_qualified ?? 0,
      });
    },
  });

  // Properties dropdown (role-scoped by backend for managers)
  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: properties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.propertyId, is_active: '1' },
    dependency: [dropdownSearchData.propertyId],
  });

  // Visit types
  const { data: allVisitTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitType, searchText: '' },
    dependency: [],
  });

  const { data: visitTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitType, searchText: dropdownSearchData.visitType },
    dependency: [dropdownSearchData.visitType],
  });

  // User roles
  const { data: allUserRoles } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitUserRole, searchText: '' },
    dependency: [],
  });

  const { data: userRoles } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitUserRole, searchText: dropdownSearchData.userRole },
    dependency: [dropdownSearchData.userRole],
  });

  // Service types — needed to derive cleaner's service type ID for vendor lookup
  const { data: allServiceTypes } = useFetch({
    service: VendorService,
    func: 'serviceTypesLookup',
    payload: { searchText: '' },
    dependency: [],
  });
  const cleanerServiceTypeId = ((allServiceTypes as any[]) || []).find((s: any) => s.name?.toLowerCase() === 'cleaner')?.id?.toString() ?? '';

  // Roles lookup — needed to get numeric role_id for user lookup
  const { data: allRolesLookup } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [],
  });
  const systemRoleName = PROPERTY_VISIT_ROLE_TO_SYSTEM_ROLE[filters.userRole] ?? '';
  const visitedByRoleId = ((allRolesLookup as any[]) || []).find((r: any) => r.name?.toLowerCase() === systemRoleName.toLowerCase())?.id?.toString() ?? '';

  // Visited by — depends on selected role filter
  const { data: allVisitedByOptions } = useFetch({
    service: PropertyVisitService,
    func: 'visitedByLookup',
    payload: { searchText: '', role: filters.userRole, roleId: visitedByRoleId, cleanerServiceTypeId },
    dependency: [filters.userRole, visitedByRoleId, cleanerServiceTypeId],
  });

  const { data: visitedByOptions } = useFetch({
    service: PropertyVisitService,
    func: 'visitedByLookup',
    payload: { searchText: dropdownSearchData.visitedBy, role: filters.userRole, roleId: visitedByRoleId, cleanerServiceTypeId },
    dependency: [dropdownSearchData.visitedBy, filters.userRole, visitedByRoleId, cleanerServiceTypeId],
  });

  // Property managers — for "Property Managed by" filter
  const { data: allManagerOptions } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: 1 },
    dependency: [],
  });

  const { data: managerOptions } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.managedByUserId, is_active: 1 },
    dependency: [dropdownSearchData.managedByUserId],
  });

  // Visit statuses
  const { data: allVisitStatuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitStatus, searchText: '' },
    dependency: [],
  });

  const { data: visitStatuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitStatus, searchText: dropdownSearchData.visitStatus },
    dependency: [dropdownSearchData.visitStatus],
  });

  const handleApplyFilters = useCallback(
    (newFilters: ListFilters) => {
      setPage(1);
      setFilters(newFilters);
      saveFilters(newFilters);
    },
    [saveFilters, setPage]
  );

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters({
      ...getStartEndOfMonth(),
      propertyId: '',
      visitType: '',
      userRole: roleUserRole,
      visitedBy: roleVisitedBy,
      visitStatus: '',
      searchText: '',
      managedByUserId: '',
    });
    resetFilters();
  }, [resetFilters, setPage, roleUserRole, roleVisitedBy]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleRemoveClick = (id: string) => {
    setRemovalId(id);
  };

  const handleRemoveVisit = useCallback(async () => {
    try {
      const response: any = await PropertyVisitService.deleteVisit(removalId);
      if (response?.success !== false) {
        setCounter((prev) => prev + 1);
        toast.success(
          <Toast message={APP_SUCCESS_MESSAGES.propertyVisitDeleted} />,
          { icon: false }
        );
      }
      setRemovalId('');
    } catch {
      setRemovalId('');
    }
  }, [removalId]);

  const modalAction = (
    <div className="d-flex gap-3">
      <Button
        className="btn btn-outline-secondary"
        text={t('propertyVisits.list.pageTexts.table.confirmModal.cancelBtn')}
        onClick={() => setRemovalId('')}
      />
      <Button
        className="btn btn-danger"
        text={t('propertyVisits.list.pageTexts.table.confirmModal.confirmBtn')}
        onClick={handleRemoveVisit}
      />
    </div>
  );

  const headerSection = <TableHeaderSection total={total} kpis={kpis} />;

  const tableValues: TableContextProvider = useMemo(
    () => ({
      rowProps: { handleRemoveClick },
      headerSection,
      row: Row,
      data: (visits as any)?.records || visits || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('propertyVisits.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, order, orderBy, page, perPage, total, loading, visits, setPage, setPerPage, setOrder, setOrderBy]
  );

  return (
    <>
      <Header />

      <Filters
        visitTypes={visitTypes || []}
        allVisitTypes={allVisitTypes || []}
        userRoles={userRoles || []}
        allUserRoles={allUserRoles || []}
        visitedByOptions={visitedByOptions || []}
        allVisitedByOptions={allVisitedByOptions || []}
        visitStatuses={visitStatuses || []}
        allVisitStatuses={allVisitStatuses || []}
        properties={properties || []}
        allProperties={allProperties || []}
        managerOptions={managerOptions || []}
        allManagerOptions={allManagerOptions || []}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        isInspector={isInspector}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={tableValues} />

      <Modal
        open={!!removalId}
        onClose={() => setRemovalId('')}
        title={t('propertyVisits.list.pageTexts.table.confirmModal.title')}
        content={t('propertyVisits.list.pageTexts.table.confirmModal.content')}
        action={modalAction}
        contentClassName="text-center"
        maxWidth="sm"
      />
    </>
  );
};

export default List;
